'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';

export default function LandingPage() {
  const { user, loading } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500" />
            <span className="text-2xl font-bold text-primary-700">Tandem</span>
          </div>
          <div className="flex items-center space-x-4">
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-gray-500" aria-label="Checking authentication" />
            ) : user ? (
              <Link href="/dashboard">
                <Button>Dashboard</Button>
              </Link>
            ) : (
              <>
                <Link href="/login">
                  <Button variant="ghost">Log in</Button>
                </Link>
                <Link href="/signup">
                  <Button>Get Started</Button>
                </Link>
              </>
            )}
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <main className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Split expenses with your partner,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">
              naturally
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Stop arguing about who paid for what. Tandem uses AI to help couples split transactions
            with context, not confusion.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link href="/signup">
              <Button size="lg">Start Free</Button>
            </Link>
            <Link href="#features">
              <Button variant="outline" size="lg">
                Learn More
              </Button>
            </Link>
          </div>
        </div>

        {/* Features Section */}
        <div id="features" className="mt-32 max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            How Tandem Makes Splitting Easy
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <Card>
              <CardContent className="p-8">
                <div className="h-12 w-12 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center text-2xl mb-4">
                  📊
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Upload Bank Statements</h3>
                <p className="text-gray-600">
                  Import CSV or PDF bank statements from any Australian bank. We parse and organize
                  everything automatically.
                </p>
              </CardContent>
            </Card>

            {/* Feature 2 */}
            <Card>
              <CardContent className="p-8">
                <div className="h-12 w-12 rounded-lg bg-secondary-100 text-secondary-600 flex items-center justify-center text-2xl mb-4">
                  🤖
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">AI Enrichment</h3>
                <p className="text-gray-600">
                  Our AI identifies merchants, categorizes transactions, and provides context about
                  what you did each day.
                </p>
              </CardContent>
            </Card>

            {/* Feature 3 */}
            <Card>
              <CardContent className="p-8">
                <div className="h-12 w-12 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center text-2xl mb-4">
                  📅
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Day-by-Day View</h3>
                <p className="text-gray-600">
                  Split transactions day-by-day with calendar integration. Remember what you did and
                  split accordingly.
                </p>
              </CardContent>
            </Card>

            {/* Feature 4 */}
            <Card>
              <CardContent className="p-8">
                <div className="h-12 w-12 rounded-lg bg-secondary-100 text-secondary-600 flex items-center justify-center text-2xl mb-4">
                  ⚡
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Smart Rules</h3>
                <p className="text-gray-600">
                  Create rules to auto-split recurring expenses. Netflix always 50/50? Done. Rent?
                  Set it once.
                </p>
              </CardContent>
            </Card>

            {/* Feature 5 */}
            <Card>
              <CardContent className="p-8">
                <div className="h-12 w-12 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center text-2xl mb-4">
                  💡
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">AI Suggestions</h3>
                <p className="text-gray-600">
                  Based on your patterns, we suggest how to split each transaction. Accept or adjust
                  as needed.
                </p>
              </CardContent>
            </Card>

            {/* Feature 6 */}
            <Card>
              <CardContent className="p-8">
                <div className="h-12 w-12 rounded-lg bg-secondary-100 text-secondary-600 flex items-center justify-center text-2xl mb-4">
                  🎯
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Settlement Tracking</h3>
                <p className="text-gray-600">
                  See who owes whom at a glance. Mark payments as settled and keep track of your
                  balance.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-32 max-w-4xl mx-auto text-center bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to split smarter?</h2>
          <p className="text-lg mb-8 text-primary-50">
            Join couples who are spending less time arguing about money and more time enjoying life
            together.
          </p>
          <Link href="/signup">
            <Button size="lg" className="bg-white text-primary-600 hover:bg-gray-50">
              Get Started Free
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-32 border-t border-gray-200">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <p>&copy; 2025 Tandem. All rights reserved.</p>
            <div className="flex items-center space-x-6">
              <a href="#" className="hover:text-gray-900 transition-colors">
                Privacy
              </a>
              <a href="#" className="hover:text-gray-900 transition-colors">
                Terms
              </a>
              <a href="#" className="hover:text-gray-900 transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
